import { ReactElement, useState } from 'react'
import { Stack, Typography } from '@mui/material'
import AIInstructionForm from './AIInstructionForm'
import AIInstructionRow from './AIInstructionRow'

interface Props {
  instructions: { id: string; text: string }[]
  emptyText: string
  /** Hides add, edit and delete. */
  readOnly?: boolean
  isCreating: boolean
  updatingId?: string
  deletingId?: string
  /** Rejects on failure (shown by the mutation's onError); the add form then keeps its text. */
  onCreate: (text: string) => Promise<unknown>
  onUpdate: (instructionId: string, text: string) => Promise<unknown>
  onDelete: (instructionId: string) => void
  addFormId: string
  'data-cy'?: string
}

/** Editable list of KI-Instruktionen of one scope, with the add form below. */
const AIInstructionList = ({
  instructions,
  emptyText,
  readOnly = false,
  isCreating,
  updatingId,
  deletingId,
  onCreate,
  onUpdate,
  onDelete,
  addFormId,
  'data-cy': dataCy,
}: Props): ReactElement => {
  // Remounts the add form after a successful add, which clears it.
  const [addFormKey, setAddFormKey] = useState(0)

  const handleCreate = async (text: string) => {
    try {
      await onCreate(text)
      setAddFormKey(key => key + 1)
    } catch {
      // Already shown as a snackbar: keep the text in the form.
    }
  }

  return (
    <Stack spacing={1} data-cy={dataCy}>
      {instructions.length === 0 && (
        <Typography variant="body2" color="text.secondary" data-cy="ai-instructions-empty">
          {emptyText}
        </Typography>
      )}
      {instructions.map(instruction => (
        <AIInstructionRow
          key={instruction.id}
          instruction={instruction}
          isUpdating={updatingId === instruction.id}
          isDeleting={deletingId === instruction.id}
          onUpdate={readOnly ? undefined : text => onUpdate(instruction.id, text)}
          onDelete={readOnly ? undefined : () => onDelete(instruction.id)}
        />
      ))}
      {!readOnly && (
        <AIInstructionForm
          key={addFormKey}
          id={addFormId}
          submitText="Hinzufügen"
          isLoading={isCreating}
          onSubmit={handleCreate}
          data-cy={addFormId}
        />
      )}
    </Stack>
  )
}

export default AIInstructionList
